/*
 * main.js
 *
 * Application entry. Vite imports this from index.html.
 *
 * The startup flow today:
 *   1. Load styles.
 *   2. Publish action handlers on window for inline onclick attrs.
 *   3. Render the app from hardcoded seed data.
 *
 * After migration step 5, this file gains an auth gate:
 *   1. Load styles.
 *   2. Check for an existing Supabase session.
 *   3. If signed out → mount auth gate, wait for sign-in.
 *   4. If signed in  → fetch state from Supabase → render.
 */

import './styles/theme.css';
import './styles/app.css';

import { attachToWindow, installEscapeHandler } from './actions.js';
import { render, setPerson, switchTab, switchTabById, switchSubTab } from './render.js';
import { initSwipe } from './swipe.js';
import { supabase, fetchProfile, patchProfile } from './lib/supabase.js';
import { getSession, mountAuthGate, onAuthChange, signOut } from './lib/auth.js';
import { mountSetupGate } from './lib/setup-gate.js';
import { hydrateFromSupabase } from './lib/hydrate.js';
import { state } from './state.js';
import { startOnboarding } from './lib/onboarding.js';
import { loadCache, applyCache, writeCache } from './lib/cache.js';
import { onChange as onSyncQueueChange, flush as flushSyncQueue, pendingCount } from './lib/sync-queue.js';
import { initPwaUpdate } from './lib/pwa-update.js';

// Expose render-layer functions on window for inline onclick handlers.
window.setPerson = setPerson;
window.switchTab = switchTab;
window.switchTabById = switchTabById;
window.switchSubTab = switchSubTab;
window.signOut = signOut;
attachToWindow();
installEscapeHandler();

// Row-swipe handler één keer mounten op document-niveau. Tab-swipe is in
// 2026-06-20 verwijderd ten voordele van de mobile bottom-nav — gaf te veel
// per-ongeluk-tab-switches en visuele glitches op kleine schermen.
initSwipe();

// Service-worker registreren + update-toast wiren. Doet niets in dev.
initPwaUpdate();

// Pending-sync badge: bij elke queue-length change tonen/verbergen.
// onSyncQueueChange fired direct met de huidige lengte, dus dit werkt
// ook al voordat de eerste render heeft plaatsgevonden (we kijken op
// elke call eerst of het element al bestaat).
onSyncQueueChange((n) => {
  // Eerste callback komt vóór de shell gemount is (onChange fired direct
  // bij subscribe). Retry op next-frame tot het element bestaat, gecapt
  // op ~500ms zodat we niet eeuwig blijven proberen als render om
  // onverklaarbare reden niet gebeurt.
  let tries = 0;
  const apply = () => {
    const el = document.getElementById('sync-indicator');
    if (!el) {
      if (tries++ < 30) requestAnimationFrame(apply);
      return;
    }
    if (n > 0) {
      el.textContent = `${n} wijziging${n === 1 ? '' : 'en'} wacht`;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  };
  apply();
});

/**
 * Boot-flow:
 *   1. Geen Supabase config → val terug op hardcoded seed (handig tijdens dev
 *      en vóór .env.local is ingesteld).
 *   2. Geen sessie → toon login-gate.
 *   3. Sessie + nog geen huishouden → toon setup-gate (1e keer setup of
 *      partner die aansluit via ?invite=).
 *   4. Sessie + huishouden → ga de app in.
 *
 * Reads vanuit Supabase (stap 6) en local-first sync (stap 8) haken in
 * `enterApp()`: dáár hoort de hydrate-call straks bij.
 */
async function boot() {
  if (!supabase) {
    console.warn('Supabase niet geconfigureerd — app draait op hardcoded seed-data.');
    render();
    return;
  }

  const session = await getSession();
  if (!session) {
    mountAuthGate((newSession) => continueAfterLogin(newSession));
    return;
  }
  continueAfterLogin(session);
}

/**
 * Na een geldige sessie: haal het profile op, kies tussen setup-gate of
 * meteen de app in. Wordt zowel bij bestaande sessie (boot-pad 4) als
 * direct na magic-link-login (terugkeer vanuit mountAuthGate) aangeroepen.
 */
async function continueAfterLogin(session) {
  // Local-first pijler 1: probeer eerst een gecachete state te tonen, maar
  // wacht maximaal 300ms — zodat een snelle hydrate gewoon direct de
  // verse data laat zien en we geen onnodige flash hebben. Bij trage
  // verbinding (of offline na 1× hydrate) komt de cache voor de echte
  // data binnen is. Cache wordt vervangen zodra hydrate succesvol klaar is.
  const cached = loadCache(session.user.id);
  let firstRenderDone = false;

  let cacheTimer = null;
  if (cached) {
    cacheTimer = setTimeout(() => {
      if (firstRenderDone) return;
      firstRenderDone = true;
      applyCache(cached);
      render();
    }, 300);
  }

  let profile;
  try {
    profile = await fetchProfile(session.user.id);
  } catch (err) {
    console.error('Profile laden mislukt:', err);
    // Stop het timer; we showen ofwel de cache nu OR een seed-render.
    if (cacheTimer) clearTimeout(cacheTimer);
    if (!firstRenderDone && cached) {
      applyCache(cached);
      render();
      firstRenderDone = true;
      // Best-effort onAuthChange-listener (geen onboarding bij cache-only).
      onAuthChange(s => { if (!s) location.reload(); });
      return;
    }
    enterApp(session, { skipHydrate: true });
    return;
  }

  if (!profile.household_id) {
    if (cacheTimer) clearTimeout(cacheTimer);
    firstRenderDone = true;
    const inviteFromUrl = readAndConsumeInvite();
    mountSetupGate(profile, inviteFromUrl, () => enterApp(session));
    return;
  }

  // Profile-check OK; nu de volledige hydrate. enterApp regelt de render +
  // onboarding + auth-listener. Daarna schrijven we de verse state als cache.
  if (cacheTimer) clearTimeout(cacheTimer);
  await enterApp(session, { cacheAlreadyShown: firstRenderDone });
  writeCache();
}

async function enterApp(session, { skipHydrate = false, cacheAlreadyShown = false } = {}) {
  // Ruim eventuele ?invite=-param op zodat hij niet eeuwig in de URL blijft staan.
  if (window.location.search.includes('invite=')) {
    history.replaceState({}, '', window.location.pathname);
  }

  // Stap 6 — Reads: trek alle persoonlijke + huishoudelijke data uit Supabase.
  // Bij fout: log en val terug op de hardcoded seed zodat de gebruiker niet
  // op een wit scherm achterblijft (state.S is al gevuld met defaults).
  if (!skipHydrate) {
    try {
      await hydrateFromSupabase(session);
    } catch (err) {
      console.error('Hydrate uit Supabase mislukt — val terug op seed:', err);
    }
  }

  render();

  // Eerste-keer-tutorial. Hydrate zet onboardingOpen op !profile.onboarding_done;
  // we starten 'm op de volgende frame zodat de DOM zeker bestaat voor de
  // tour-module targets kan vinden via querySelector. Op skip/finish:
  // markeer 't profile zodat de tour niet meer terugkomt.
  if (state.onboardingOpen && state.userId) {
    requestAnimationFrame(() => {
      startOnboarding(() => {
        state.onboardingOpen = false;
        patchProfile(state.userId, { onboarding_done: true })
          .catch(err => console.warn('Sync onboarding_done failed:', err));
      });
    });
  }

  // Bij sign-out: reload → keer terug naar de login-gate.
  onAuthChange(newSession => {
    if (!newSession) location.reload();
  });
}

/**
 * Lees een eventuele ?invite=<uuid> uit de URL, of uit localStorage (als
 * fallback voor browsers waar de magic-link-redirect query-params verloor).
 * Verwijdert 'm meteen uit localStorage zodat een latere login niet
 * per ongeluk opnieuw gepre-fild wordt.
 */
function readAndConsumeInvite() {
  const fromUrl = new URLSearchParams(window.location.search).get('invite');
  if (fromUrl) {
    // Sla 'm óók even op voor het geval een mid-flow refresh hem uit de URL haalt.
    try { localStorage.setItem('florijn:invite', fromUrl); } catch {}
    return fromUrl;
  }
  try {
    const stored = localStorage.getItem('florijn:invite');
    if (stored) {
      localStorage.removeItem('florijn:invite');
      return stored;
    }
  } catch {}
  return null;
}

boot();

// ─────────────────────────────────────────────────────────────
// Cross-device refresh: bij tab-focus / online-komst opnieuw uit Supabase
// trekken zodat wijzigingen op een ander toestel zichtbaar worden zonder
// dat de gebruiker zelf moet refreshen. Geen echte realtime sync (stap 8
// in MIGRATION_PLAN), maar wel "best-effort: zo snel mogelijk gelijk".
// ─────────────────────────────────────────────────────────────
let lastRehydrateAt = 0;
const REHYDRATE_THROTTLE_MS = 8_000;

async function tryRehydrate(reason) {
  if (!state.userId) return;
  // Niet onderbreken terwijl een modal/edit actief is — anders verlies je
  // ingetypte (nog niet opgeslagen) waarden door de re-render.
  if (state.modalOpen || state.addItemModal || state.pendingAdd) return;
  if (state.modalAutoNoticeOpen || state.modalSpaarSkipOpen) return;
  if (state.editing && Object.values(state.editing).some(Boolean)) return;

  const now = Date.now();
  if (now - lastRehydrateAt < REHYDRATE_THROTTLE_MS) return;
  lastRehydrateAt = now;

  try {
    // Eerst de pending-queue uitleegmaken zodat lokale writes UP gaan
    // vóór we de server-state binnentrekken — anders zou hydrate ze
    // overschrijven met de oudere remote versie.
    if (pendingCount() > 0) await flushSyncQueue();

    const session = await getSession();
    if (!session) return;
    await hydrateFromSupabase(session);
    writeCache();
    render();
  } catch (err) {
    console.warn(`Re-hydrate (${reason}) mislukt:`, err);
  }
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryRehydrate('visibility');
  });
  window.addEventListener('focus',  () => tryRehydrate('focus'));
  window.addEventListener('online', () => tryRehydrate('online'));
}
